<script setup lang="ts">
import { computed, type Ref, ref, watch } from 'vue'

import { ProfileOptionItem, Selector } from '../../'
import { type CreatorsV2Filter, type UserItem } from '../../../types'
import { debounce } from '../../../utils'

type UseGetCreatorsReturn = {
  data: Ref<UserItem[]>
  isLoading: Ref<boolean>
  error: Ref<Error | null>
}

type CreatorOption = UserItem & { label: string }

const props = withDefaults(
  defineProps<{
    placeholder?: string
    advertiserGroupId?: number
    advertiserId?: number
    shouldTruncateText?: boolean
    width?: string | number
    loadCreators: (filterRef: Ref<CreatorsV2Filter>) => UseGetCreatorsReturn
  }>(),
  {
    placeholder: 'Search creators by ID, email or username',
  },
)
const emits = defineEmits(['update:creator'])
const filter = ref<CreatorsV2Filter>({
  page: 1,
  ...(props.advertiserGroupId && {
    advertiserGroup: {
      id: props.advertiserGroupId,
      name: '',
    },
  }),
  ...(props.advertiserId && {
    advertiser: {
      id: props.advertiserId,
      label: '',
    },
  }),
  query: '',
})
const creator = defineModel<CreatorsV2Filter['creator']>('creator')
const { data, isLoading } = props.loadCreators(filter)

const creatorsOptions = computed(() =>
  data.value
    .filter((item) => {
      return [(item.username ?? '').toLowerCase(), (item.email ?? '').toLowerCase(), (item.id ?? '').toString()].some((field) =>
        field.includes(filter.value.query?.toLowerCase() ?? ''),
      )
    })
    .map((item) => {
      return {
        ...item,
        label: item.email ? `[ID: ${item.id}] ${item.username} - ${item.email}` : item.username || `ID:${item.id}`,
      }
    }),
)
const creatorLabel = computed(() => {
  if (creator.value) {
    const { id, username } = creator.value
    const found = data.value.find((item) => item.id === id)

    return username || found?.username || `ID:${id}`
  }
  return 'Creators'
})

const handleSearchQuery = debounce((value: string) => (filter.value.query = value), 500)
const handleCreatorSelection = (item: { id: number; label?: string } | null) => {
  if (!item) {
    return emits('update:creator', undefined)
  }
  emits('update:creator', item.id === creator.value?.id ? undefined : item)
}
const handleVisible = () => (filter.value.query = '')

watch(
  () => [props.advertiserGroupId, props.advertiserId],
  ([advertiserGroupId, advertiserId]) => {
    filter.value.query = ''
    filter.value.advertiserGroup = advertiserGroupId ? { id: advertiserGroupId, name: '' } : undefined
    filter.value.advertiser = advertiserId ? { id: advertiserId, label: '' } : undefined
  },
)
</script>
<template>
  <Selector
    :options="creatorsOptions"
    :selected-option="creator"
    :selected-label="creatorLabel"
    :show-search="true"
    :loading="isLoading"
    :placeholder="placeholder"
    :should-truncate-text="shouldTruncateText"
    show-clear-button
    :query="filter.query"
    :width="width"
    @search="handleSearchQuery"
    @update:selected-option="handleCreatorSelection"
    @visible-change="handleVisible"
  >
    <template #option="{ item, selected }">
      <ProfileOptionItem
        :id="item.id"
        :username="(item as CreatorOption).username"
        :email="(item as CreatorOption).email"
        :image="(item as CreatorOption).image"
        :avatar-name="(item as CreatorOption).displayName"
        :added="selected"
        indicator="check"
      />
    </template>
  </Selector>
</template>
